import type { StateStorage } from 'zustand/middleware'

type BrowserStorageGetter = () => Storage | undefined

const BROWSER_STORAGE_RETRY_INTERVAL_MS = 10_000

const getLocalStorage = (): Storage | undefined => {
  if (typeof window === 'undefined') return undefined

  try {
    return window.localStorage
  } catch {
    return undefined
  }
}

export const createResilientStorage = (
  getBrowserStorage: BrowserStorageGetter = getLocalStorage
): StateStorage => {
  const memoryItems = new Map<string, string>()
  let retryBrowserStorageAfter = 0

  const markBrowserStorageUnavailable = () => {
    retryBrowserStorageAfter = Date.now() + BROWSER_STORAGE_RETRY_INTERVAL_MS
  }

  const getStorage = () => {
    if (Date.now() < retryBrowserStorageAfter) return undefined

    try {
      const storage = getBrowserStorage()
      if (!storage) {
        markBrowserStorageUnavailable()
        return undefined
      }

      retryBrowserStorageAfter = 0
      return storage
    } catch {
      markBrowserStorageUnavailable()
      return undefined
    }
  }

  const flushMemoryItem = (storage: Storage, name: string) => {
    const memoryValue = memoryItems.get(name)
    if (memoryValue === undefined) return true

    try {
      storage.setItem(name, memoryValue)
      memoryItems.delete(name)
      retryBrowserStorageAfter = 0
      return true
    } catch {
      markBrowserStorageUnavailable()
      return false
    }
  }

  return {
    getItem: name => {
      const memoryValue = memoryItems.get(name)
      const storage = getStorage()
      if (!storage) return memoryValue ?? null

      if (memoryValue !== undefined && !flushMemoryItem(storage, name)) {
        return memoryValue
      }

      try {
        return storage.getItem(name) ?? null
      } catch {
        markBrowserStorageUnavailable()
        return memoryItems.get(name) ?? null
      }
    },
    setItem: (name, value) => {
      memoryItems.set(name, value)

      const storage = getStorage()
      if (!storage) return

      try {
        storage.setItem(name, value)
        memoryItems.delete(name)
        retryBrowserStorageAfter = 0
      } catch {
        markBrowserStorageUnavailable()
      }
    },
    removeItem: name => {
      memoryItems.delete(name)

      const storage = getStorage()
      if (!storage) return

      try {
        storage.removeItem(name)
        retryBrowserStorageAfter = 0
      } catch {
        markBrowserStorageUnavailable()
      }
    },
  }
}
